import { Loader2, RotateCw, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Kbd } from '@/components/ui/kbd';
import { Panel } from '@/components/ui/panel';
import {
    useTokenPairs,
    useTokenPairStore,
    useVolumeOrder,
} from '@/features/tokenPairs';

import { useFavoriteMarkets } from '../hooks/useFavoriteMarkets';
import { selectHeldCoins, useTradingStore } from '../store';

import { MarketRow } from './MarketRow';

/**
 * Left-column market list (wide desktop only): search + one row per market
 * with mark price, funding, and max leverage.
 *
 * Switching markets used to be locked while a position was open, because only
 * one position could exist and leaving its market would have stranded it. One
 * position PER COIN is allowed now, so switching is the primary navigation:
 * it is how you reach the market you want to open next, and — under Option A,
 * where the rail follows the chart — how you get back to manage one you hold.
 * A row for a market you already hold is marked rather than disabled.
 *
 * Memoized (no props): Trading re-renders at ~15Hz with the live price, and
 * ~230 market rows must not re-render with it.
 */
const MarketsSidebar: React.FC = React.memo(() => {
    const [query, setQuery] = useState('');

    const { selectedCoin, setSelectedCoin, networkId } = useTokenPairStore();
    // Keep the snapshot prices/funding reasonably fresh while the sidebar is
    // on screen; the selected row streams live via LiveSelectedPrice.
    const { data, isLoading, isError, refetch } = useTokenPairs(networkId, {
        refetchInterval: 30_000,
    });
    // markets this client holds a position on — marked in the list so switching
    // away from one is obviously reversible.
    const heldCoins = useTradingStore(selectHeldCoins);
    const { favorites, toggleFavorite } = useFavoriteMarkets();

    // Busiest market first. The ranking is held still across the 30s refetch —
    // see useVolumeOrder for why a live re-sort is a mis-click risk here.
    const byVolume = useVolumeOrder(data?.markets ?? []);

    const markets = useMemo(() => {
        const q = query.trim().toLowerCase();
        const filtered = q
            ? byVolume.filter((m) => m.coin.toLowerCase().includes(q))
            : byVolume;
        if (favorites.length === 0) return filtered;
        // Pinned markets first; sort() is stable, so the volume order holds
        // within each group.
        const pinned = new Set(favorites);
        return [...filtered].sort(
            (a, b) => Number(pinned.has(b.coin)) - Number(pinned.has(a.coin)),
        );
    }, [byVolume, query, favorites]);

    return (
        <Panel
            as="aside"
            aria-label="Markets"
            className="flex flex-col"
            style={{ gridArea: 'markets' }}
        >
            {/* Search */}
            <div className="flex items-center gap-2 border-b border-border px-3">
                <Search size={13} className="shrink-0 text-muted-foreground" />
                <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label="Search markets"
                    placeholder="Search markets"
                    className="h-10 w-full border-0 bg-transparent p-0 text-xs text-foreground outline-none placeholder:text-muted-foreground/70 focus:border-0 focus:outline-none focus:ring-0"
                />
            </div>

            {/* List */}
            <div className="min-h-0 flex-1 overflow-y-auto py-1">
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <Loader2
                            size={16}
                            className="animate-spin text-muted-foreground"
                        />
                    </div>
                )}
                {isError && (
                    <div className="flex flex-col items-center gap-2 px-3 py-8 text-center">
                        <span className="text-xs text-muted-foreground">
                            Markets unavailable.
                        </span>
                        <button
                            onClick={() => void refetch()}
                            className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-secondary"
                        >
                            <RotateCw size={11} />
                            Retry
                        </button>
                    </div>
                )}
                {!isLoading && !isError && markets.length === 0 && (
                    <p className="px-3 py-6 text-center text-xs text-muted-foreground">
                        No market matches "{query}".
                    </p>
                )}
                {markets.map((market) => (
                    <MarketRow
                        key={market.coin}
                        market={market}
                        isSelected={
                            market.coin.toLowerCase() ===
                            selectedCoin.toLowerCase()
                        }
                        held={heldCoins.has(market.coin.toUpperCase())}
                        onSelect={() => setSelectedCoin(market.coin)}
                        favorite={favorites.includes(market.coin)}
                        onToggleFavorite={() => toggleFavorite(market.coin)}
                    />
                ))}
            </div>

            {/* Footer hint */}
            <div className="flex items-center justify-center gap-1.5 border-t border-border py-2 text-[10px] text-muted-foreground">
                <Kbd>⌘K</Kbd>
                <span>quick switch</span>
            </div>
        </Panel>
    );
});

MarketsSidebar.displayName = 'MarketsSidebar';

export default MarketsSidebar;
